import { useNumberMask } from "datepicker";
import React, { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { formatNumber, sleep } from "simple-utils";
import { nextMonth } from "simple-utils/dist/date-utils";
import { Loading } from "../../components/Loading";

import { Modal } from "../../components/Modal";
import { load } from "../../loaders/basic-loader";
import { bigNumber, formatMoney } from "../../utils/money-utils";
import { AccountContext } from "../AccountContext";

type CalcInputState = {
    lead: number | null
    cpl: number | null
    cost: number | null
}

type FieldName = keyof CalcInputState;

type LeadCostReport = {leads:number, cost:number, numDays: number, numAccounts: number}

function AvgInfoDisplay({onLoad}:{onLoad:(s:CalcInputState)=>void}){
    const [report, setReport] = useState<LeadCostReport>();
    const [info, setInfo] = useState<CalcInputState>();
    const {filtered} = useContext(AccountContext)

    useEffect(function(){
        (async ()=>{
            if(!filtered || !filtered.length) return;
            try{
                const result = (await load("api/v1/lead-cost-report", {}, {ids: filtered.map(i => i.id).join(', ')})) as LeadCostReport;
                if(result.numDays == 0) throw "Dados insuficientes";
                const lead = result.leads / result.numDays * 30;
                const cost = result.cost / result.numDays * 30;
                const i = {lead: lead / result.numAccounts, cost: cost / result.numAccounts, cpl: cost / lead};
                setInfo(i);
                setReport(result);
                onLoad(i);
            }catch(e){
                console.error(e);
            }
        })()
    }, [])

    if(report == null)
        return <Loading className="mb-3" />

    return <div className="mb-3">
        <div className="fw-bold fs-5 mb-2">Media baseada no filtro atual:</div>
        <div className="d-flex justify-content-between align-items-center">
            <div className="lh-base">
                <b>Contas utilizadas:</b> {report.numAccounts}<br />
                <b>Leads Médio:</b> {bigNumber(info.lead)}<br />
                <b>CPL Médio:</b> {formatMoney(info.cpl)}<br />
                <b>Investimento Médio:</b> {formatMoney(info.cost)}
            </div>
            <button className="ms-2 btn btn-primary" onClick={() => onLoad(info)}><i className="fa fa-refresh"></i></button>
        </div>
    </div>
}


function CalcInput({name, setter, state, change}:{change: number, name: FieldName, state:CalcInputState, setter:(v:CalcInputState)=>void}){
    const ref = useRef<HTMLInputElement>();
    function onChange(e:ChangeEvent<HTMLInputElement>){
        var newValue = parseFloat(e.target.value.replace(/,/, '.'));
        if(isNaN(newValue))
            return;
        setter({...state, [name]: newValue});
    }

    useEffect(function(){
        ref.current.value = formatNumber(state[name], 2);
    }, [change]);

    return <div className="col-4">
        <div className="mb-1">{translateLabel(name)}</div>
        <input className="form-control" onChange={onChange} ref={ref} />
    </div>
}

function ResultLine({label, formula, value}:{label:string, formula:string, value:number}){
    return <div className="mt-3"><b>{label}:</b> <span className="text-muted">{formula}</span> {formatNumber(value, 2)}</div>
}


function isValid(a: number, b: number){
    if(!a) return false;
    if(!b) return false;
    return true;
}

function translateLabel(f:FieldName){
    switch(f){
        case "cost": return "Investimento";
        case "cpl": return "CPL";
        case "lead": return "Leads";
    }
}


function CalcModal(){
    const [info, setInfo] = useState<CalcInputState>(null);
    const [field, setField] = useState("cost");
    const [change, setChange] = useState(0);
    
    return <>
        <AvgInfoDisplay onLoad={(e) => {setChange( i => i + 1); setInfo(e); } }  />
        {info != null && <>
        <div style={{maxWidth:"300px"}}>
            <div className="mb-1">Calcular</div>
            <select className="form-select" value={field} onChange={(e)=>setField(e.target.value)}>
                {(["cost", "lead", "cpl"] as FieldName[]).map(i => <option value={i}>{translateLabel(i)}</option>)}
            </select>
        </div>


        <div className="row mt-3">
            {field != "lead" && <CalcInput name="lead" state={info} setter={setInfo} change={change} />}
            {field != "cpl" && <CalcInput name="cpl" state={info} setter={setInfo} change={change} />}
            {field != "cost" && <CalcInput name="cost" state={info} setter={setInfo} change={change} />}
        </div>
        
        {field == 'lead' && isValid(info.cpl, info.cost) && <ResultLine label="Leads" formula={`${formatNumber(info.cost, 2)} / ${formatNumber(info.cpl, 2)} →`} value={info.cost / info.cpl} />}
        {field == 'cpl' && isValid(info.cost, info.lead) && <ResultLine label="CPL" formula={`${formatNumber(info.cost, 2)} / ${formatNumber(info.lead, 2)} →`} value={info.cost / info.lead} />}
        {field == 'cost' && isValid(info.cpl, info.lead) && <ResultLine label="Investimento" formula={`${formatNumber(info.lead, 2)} * ${formatNumber(info.cpl, 2)} →`}value={info.lead * info.cpl} />}
        </>}
    </>
}


export function CalcButton(){
    const [show, setShow] = useState(false);

    return <>
        <button className="btn btn-primary me-2 mb-2" onClick={() => setShow(true)}><i className="fa fa-calculator"></i> Calculadora</button>
        {show && <Modal className="modal-primary" onHide={() => setShow(false)} title="Calculadora"><CalcModal /></Modal>}
    </>
}